import React, { useMemo, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelectedSchool } from '../contexts/SelectedSchoolContext';
import type { MessageState, StudentOption, SchoolOption } from '../types';
import {
  useParentStudents,
  useLinkStudent,
  useParentDelegates,
  useAddParentDelegate,
  useRemoveParentDelegate,
  usePatchParentDelegate,
} from '../api/students';
import { isValidMobileDigits, normalizeMobileForOtp } from '@uniqcamp/shared';

const Students: React.FC = () => {
  const { selectedSchoolId } = useSelectedSchool();
  const [localSchoolFilter, setLocalSchoolFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [studentUniqueId, setStudentUniqueId] = useState('');
  const [schoolCode, setSchoolCode] = useState('');
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });

  const [delegateForStudent, setDelegateForStudent] = useState<StudentOption | null>(null);
  const [newDelMobile, setNewDelMobile] = useState('');
  const [newDelName, setNewDelName] = useState('');
  const [newDelView, setNewDelView] = useState(true);
  const [newDelDispersal, setNewDelDispersal] = useState(true);
  const [delegateMessage, setDelegateMessage] = useState<MessageState>({ type: '', text: '' });

  const { data: studentsData } = useParentStudents(selectedSchoolId || undefined);
  const allStudents = useMemo<StudentOption[]>(() => studentsData?.students ?? [], [studentsData]);

  const delegateStudentId = delegateForStudent?._id;
  const { data: delegatesData, isLoading: delegatesLoading } = useParentDelegates(delegateStudentId, {
    enabled: Boolean(delegateStudentId),
  });
  const delegates = useMemo(() => delegatesData?.delegates ?? [], [delegatesData]);

  const addDelegateMut = useAddParentDelegate({
    onSuccess: () => {
      setNewDelMobile('');
      setNewDelName('');
      setNewDelView(true);
      setNewDelDispersal(true);
      setDelegateMessage({ type: 'success', text: 'Delegate added. They can sign in here with their mobile number and OTP.' });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setDelegateMessage({ type: 'error', text: e.response?.data?.message ?? e.message ?? 'Failed to add delegate' });
    },
  });

  const removeDelegateMut = useRemoveParentDelegate({
    onSuccess: () => setDelegateMessage({ type: 'success', text: 'Delegate removed.' }),
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setDelegateMessage({ type: 'error', text: e.response?.data?.message ?? e.message ?? 'Failed to remove' });
    },
  });

  const patchDelegateMut = usePatchParentDelegate({
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setDelegateMessage({ type: 'error', text: e.response?.data?.message ?? e.message ?? 'Failed to update' });
    },
  });

  const linkMutation = useLinkStudent({
    onSuccess: () => {
      setAddOpen(false);
      setStudentUniqueId('');
      setSchoolCode('');
      setMessage({ type: 'success', text: 'Student linked successfully.' });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setMessage({ type: 'error', text: e.response?.data?.message ?? e.message ?? 'Error' });
    },
  });

  const schools: SchoolOption[] = useMemo(() => {
    const map = new Map<string, SchoolOption>();
    allStudents.forEach((s) => {
      const school = s.schoolId;
      if (school && !map.has(school._id)) {
        map.set(school._id, { _id: school._id, name: school.name, uniqueCode: school.uniqueCode });
      }
    });
    return Array.from(map.values());
  }, [allStudents]);

  const filterSchoolId = selectedSchoolId || localSchoolFilter;
  const students = useMemo(() => {
    if (!filterSchoolId) return allStudents;
    return allStudents.filter((s) => s.schoolId?._id === filterSchoolId);
  }, [allStudents, filterSchoolId]);

  const handleLink = () => {
    setMessage({ type: '', text: '' });
    if (!studentUniqueId.trim()) {
      setMessage({ type: 'error', text: 'Enter the student unique ID from school.' });
      return;
    }
    linkMutation.mutate({
      studentId: studentUniqueId.trim(),
      schoolCode: schoolCode.trim() || undefined,
    });
  };

  const openDelegates = (s: StudentOption) => {
    setDelegateMessage({ type: '', text: '' });
    setDelegateForStudent(s);
  };

  const closeDelegates = () => {
    setDelegateForStudent(null);
    setDelegateMessage({ type: '', text: '' });
  };

  const handleAddDelegate = () => {
    setDelegateMessage({ type: '', text: '' });
    if (!delegateStudentId || !newDelMobile.trim()) {
      setDelegateMessage({ type: 'error', text: 'Enter the delegate mobile number.' });
      return;
    }
    if (!isValidMobileDigits(newDelMobile)) {
      setDelegateMessage({ type: 'error', text: 'Enter a valid mobile number (10–15 digits).' });
      return;
    }
    addDelegateMut.mutate({
      studentId: delegateStudentId,
      mobile: normalizeMobileForOtp(newDelMobile),
      name: newDelName.trim() || undefined,
      canViewStudent: newDelView,
      canDispersal: newDelDispersal,
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>My Students</Typography>
      {schools.length > 1 && !selectedSchoolId && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Filter by school</Typography>
          <TextField select fullWidth size="small" value={localSchoolFilter} onChange={(e) => setLocalSchoolFilter(e.target.value)}>
            <MenuItem value="">All schools</MenuItem>
            {schools.map((s) => (
              <MenuItem key={s._id} value={s._id}>{s.name} ({s.uniqueCode})</MenuItem>
            ))}
          </TextField>
        </Paper>
      )}
      {message.text && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">{students.length} student{students.length !== 1 ? 's' : ''}</Typography>
        <Button startIcon={<PersonAddIcon />} variant="outlined" size="small" onClick={() => setAddOpen(true)}>Add student by ID</Button>
      </Box>
      {students.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            {allStudents.length === 0 ? 'No students linked yet. Use the unique ID provided by your school to add a student.' : 'No students in this school.'}
          </Typography>
          <Button startIcon={<PersonAddIcon />} variant="contained" sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>Add student by ID</Button>
        </Paper>
      ) : (
        <List dense>
          {students.map((s) => (
            <ListItem key={s._id} component={Paper} sx={{ mb: 1 }}>
              <ListItemAvatar><Avatar src={s.photo}>{s.name?.charAt(0)}</Avatar></ListItemAvatar>
              <ListItemText
                primary={s.name}
                secondary={(
                  <span>
                    <span>
                      {s.schoolId
                        ? `${s.schoolId.name} · Class ${s.class ?? ''}${s.section ? ` ${s.section}` : ''}`
                        : `Class ${s.class ?? ''}${s.section ? ` ${s.section}` : ''}`}
                    </span>
                    {s.viewerIsPrimaryParent === false ? (
                      <Typography component="span" variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        You are a pickup delegate. Sign in with your mobile number and OTP on this app.
                      </Typography>
                    ) : null}
                  </span>
                )}
              />
              {s.viewerIsPrimaryParent ? (
                <ListItemSecondaryAction>
                  <Button size="small" variant="outlined" onClick={() => openDelegates(s)}>Delegates</Button>
                </ListItemSecondaryAction>
              ) : null}
            </ListItem>
          ))}
        </List>
      )}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add student by unique ID</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Enter the unique student ID provided by the school. If your child is in multiple schools, enter the school code.</Typography>
          <TextField fullWidth label="Student unique ID" value={studentUniqueId} onChange={(e) => setStudentUniqueId(e.target.value)} placeholder="e.g. STU001" sx={{ mb: 2 }} />
          <TextField fullWidth label="School code (optional)" value={schoolCode} onChange={(e) => setSchoolCode(e.target.value.toUpperCase())} placeholder="e.g. ABC" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLink} disabled={linkMutation.isLoading || !studentUniqueId.trim()}>{linkMutation.isLoading ? 'Linking…' : 'Link student'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(delegateForStudent)} onClose={closeDelegates} maxWidth="sm" fullWidth>
        <DialogTitle>
          Pickup delegates
          {delegateForStudent ? ` — ${delegateForStudent.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a guardian or driver using their mobile number. They use this same parent app with their own OTP login.
            Choose whether they can see the student here and use pickup / dispersal features.
          </Typography>
          {delegateMessage.text ? (
            <Alert severity={delegateMessage.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setDelegateMessage({ type: '', text: '' })}>
              {delegateMessage.text}
            </Alert>
          ) : null}
          {delegatesLoading ? (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          ) : (
            <Stack spacing={1.5}>
              {delegates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No delegates yet.</Typography>
              ) : (
                delegates.map((d) => {
                  const uid = d.userId?._id;
                  const label = d.userId?.name || d.userId?.mobile || 'User';
                  if (!uid) return null;
                  return (
                    <Paper key={uid} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">{label}</Typography>
                          {d.userId?.mobile ? (
                            <Typography variant="caption" color="text.secondary" display="block">{d.userId.mobile}</Typography>
                          ) : null}
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={d.canViewStudent !== false}
                                  disabled={patchDelegateMut.isLoading}
                                  onChange={(e) => {
                                    if (!delegateStudentId) return;
                                    patchDelegateMut.mutate({
                                      studentId: delegateStudentId,
                                      delegateUserId: uid,
                                      canViewStudent: e.target.checked,
                                    });
                                  }}
                                />
                              )}
                              label="View student"
                            />
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={d.canDispersal !== false}
                                  disabled={patchDelegateMut.isLoading}
                                  onChange={(e) => {
                                    if (!delegateStudentId) return;
                                    patchDelegateMut.mutate({
                                      studentId: delegateStudentId,
                                      delegateUserId: uid,
                                      canDispersal: e.target.checked,
                                    });
                                  }}
                                />
                              )}
                              label="Pickup & dispersal"
                            />
                          </Stack>
                        </Box>
                        <IconButton
                          aria-label="Remove delegate"
                          size="small"
                          color="error"
                          disabled={removeDelegateMut.isLoading || !delegateStudentId}
                          onClick={() => {
                            if (!delegateStudentId) return;
                            removeDelegateMut.mutate({ studentId: delegateStudentId, delegateUserId: uid });
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  );
                })
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Add delegate</Typography>
              <TextField
                fullWidth
                size="small"
                label="Mobile number"
                value={newDelMobile}
                onChange={(e) => setNewDelMobile(e.target.value)}
                placeholder="Same format you use for OTP"
              />
              <TextField
                fullWidth
                size="small"
                label="Name (optional)"
                value={newDelName}
                onChange={(e) => setNewDelName(e.target.value)}
              />
              <FormControlLabel
                control={<Checkbox checked={newDelView} onChange={(e) => setNewDelView(e.target.checked)} size="small" />}
                label="Can view this student in the app"
              />
              <FormControlLabel
                control={<Checkbox checked={newDelDispersal} onChange={(e) => setNewDelDispersal(e.target.checked)} size="small" />}
                label="Can request pickup / dispersal"
              />
              <Button variant="contained" onClick={handleAddDelegate} disabled={addDelegateMut.isLoading || !delegateStudentId}>
                {addDelegateMut.isLoading ? 'Adding…' : 'Add delegate'}
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelegates}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Students;
